import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { api, assetUrl, isSignInExpired } from '../lib/api';
import { Badge, Btn, InstructorCard, LANGUAGE_META, Modal } from '../components';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { JoinLink } from '../components/JoinLink';
import { LazyPanel } from '../components/LazyPanel';
import { TimeRequestsPanel, requestToSessionSlot, formatRequestDays, formatRequestTimes, usePublishedAnswer } from '../components/TimeRequestsPanel';
import { StudentNoteEditor } from '../components/StudentNoteEditor';
import { SpecialtyPicker } from '../components/SpecialtyPicker';
import { CopyProfileLinkButton } from '../components/CopyProfileLinkButton';
import { Avatar } from '../components/Avatar';
import { PaymentSentBadge, PaidThroughRopidaBadge, isPaymentSent } from '../components/PaymentSentBadge';
import { PaymentProofPanel } from '../components/PaymentProofPanel';
import { DashboardMenu, DashboardLogoutButton } from '../components/DashboardMenu';
import { DASHBOARD_TABS } from '../lib/dashboardTabs';
import { greetingFor } from '../lib/greeting';
import { MESSAGES_TAB, tabFromHash, useUnreadMessages, withMessagesLabel } from '../lib/messages';
import { MessageButton } from '../components/messages/MessageButton';
import { MessagesPanel } from '../components/messages/MessagesPanel';
import { isSafeLinkUrl } from '../lib/urls';
import { fieldProps } from '../lib/a11y';
import { peso } from '../lib/format';
import { formatInTz, isSameZonedDay, utcToZonedParts, zonedDateTimeToUtc, addLocalWeeks, DEFAULT_TIMEZONE } from '../lib/timezone';
import { palette as T, alpha, type } from '../design';

// FullCalendar (+Luxon) is ~100KB gzip and only instructors ever see it — lazy-load it so
// visitors and students never download the calendar bundle.
const SessionCalendar = React.lazy(() =>
  import('../components/SessionCalendar').then((m) => ({ default: m.SessionCalendar }))
);

// recharts is ~450KB and only the Payments tab draws a chart — same treatment as the calendar.
const MonthlyEarningsChart = React.lazy(() =>
  import('../components/EarningsCharts').then((m) => ({ default: m.MonthlyEarningsChart }))
);
const TopSessionsChart = React.lazy(() =>
  import('../components/EarningsCharts').then((m) => ({ default: m.TopSessionsChart }))
);

// Session levels (mirror the backend SESSION_LEVEL). The empty value saves as null, which
// students read as "All levels" — every session made before levels existed reads that way too.
const SESSION_LEVEL_OPTIONS = [
  { value: '', label: 'All levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];
const sessionLevelLabel = (level) =>
  (SESSION_LEVEL_OPTIONS.find((o) => o.value === (level || '')) || SESSION_LEVEL_OPTIONS[0]).label;

// Most weeks one weekly run may cover. Mirrors WEEKLY_RUN_MAX_WEEKS in backend/src/lib/constants.js;
// the server refuses more with the same message, so the form only saves a round trip.
const WEEKLY_RUN_MAX_WEEKS = 12;
const WEEKLY_RUN_TOO_LONG = `You can repeat a session for up to ${WEEKLY_RUN_MAX_WEEKS} weeks. Choose ${WEEKLY_RUN_MAX_WEEKS} weeks or fewer.`;

// The Schedule tab names a pending booking the way the Bookings tab's badge does: payment sent
// once the student attached a screenshot, awaiting payment before that.
const bookingStateLabel = (b) => {
  if (b.status !== 'pending') return b.status;
  return isPaymentSent(b) ? 'payment sent' : 'awaiting payment';
};
const paymentSentCount = (s) => (s.bookings || []).filter(isPaymentSent).length;
const awaitingPaymentCount = (s) => (s.bookings || []).filter(b => b.status === 'pending' && !isPaymentSent(b)).length;

// A booking with its booking payment flags (JTP-6, JTP-23). /dashboard/instructor lacks them, so
// they come from /bookings/instructor, matched by id (`flags`). Until that read lands, or when it
// fails, a booking a booking payment holds still hides Confirm (the server refuses that confirm
// anyway); only the "Paid through Ropida" badge waits for the next read.
function withPaymentFlags(b, flags) {
  const flagged = flags?.get(b.id);
  return {
    ...b,
    coveredByPayment: flagged?.coveredByPayment ?? Boolean(b.bookingPaymentId),
    paidThroughRopida: flagged?.paidThroughRopida ?? false,
  };
}

const MONTH_SHORT =['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ropida/dashboard-instructor.jsx — Instructor dashboard (Terracotta Studio)


// Country picks the flag automatically, so instructors never hand-type an emoji. Extend as needed.
const COUNTRIES = [
  { name: 'Philippines', flag: '🇵🇭' }, { name: 'Japan', flag: '🇯🇵' },
  { name: 'China', flag: '🇨🇳' }, { name: 'Taiwan', flag: '🇹🇼' },
  { name: 'South Korea', flag: '🇰🇷' }, { name: 'United States', flag: '🇺🇸' },
  { name: 'United Kingdom', flag: '🇬🇧' }, { name: 'Canada', flag: '🇨🇦' },
  { name: 'Australia', flag: '🇦🇺' }, { name: 'Spain', flag: '🇪🇸' },
  { name: 'France', flag: '🇫🇷' }, { name: 'Germany', flag: '🇩🇪' },
];
const COUNTRY_FLAG = Object.fromEntries(COUNTRIES.map(c => [c.name, c.flag]));

// `language` is a filter CODE the marketplace filters on ('JP'/'ZH'/…) — keep it a code, not free
// text. Options come from the shared LANGUAGE_META so the dropdown and marketplace never drift.
const LANGUAGE_OPTIONS = Object.entries(LANGUAGE_META).map(([code, m]) => ({ code, label: m.label }));

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border,
  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle, background: T.white, color: T.espresso };
const labelStyle = {
  display: 'block', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label,
  fontWeight: 600, color: T.walnut, marginBottom: 6,
};
const h3Style = {
  fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body,
  color: T.espresso, margin: '24px 0 16px',
};
const reqStar = <span style={{ color: T.errorRed }}> *</span>;
const fieldErrorStyle = { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '6px 0 0' };

// Ids for the session form (#345): the dialog is named by its heading, and each field that can
// fail a check has an id its label and its error point at.
const SESSION_FORM_TITLE_ID = 'session-form-title';
const sessionFieldId = (field) => `session-${field}`;
const profileFieldId = (field) => `profile-${field}`;

const SOCIAL_LINKS = [
  { key: 'youtube', label: 'YouTube' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'twitter', label: 'Twitter / X' },
  { key: 'website', label: 'Website' },
];
// Mirrors the server's check (routes/profile.js): these links reach an href on the public profile.
const LINK_MUST_BE_WEB = (what) => `Your ${what} link must start with http:// or https://.`;

// Where the address goes once a tab link (`#/dashboard-instructor?tab=messages`) has been used.
const DASHBOARD_HASH = '#/dashboard-instructor';

function InstructorDashboard({ setPage }) {
  const isMobile = useIsMobile();
  const { user: authUser, updateProfile } = useAuth();
  // Render session times — and "today" — in the instructor's own saved timezone (TTT-14),
  // so "Classes today" counts by their day, not the browser's.
  const viewerTz = authUser?.timezone || DEFAULT_TIMEZONE;
  const { showSnackbar } = useSnackbar();
  // A link may name a tab (`?tab=messages`, the new message email).
  const [section, setSection] = React.useState(() => tabFromHash(window.location.hash, DASHBOARD_TABS.instructor) || 'home');
  const [selectedStudent, setSelectedStudent] = React.useState(null);
  // The conversation to open on the Messages tab, set when a Message on a booking starts one.
  const [openConversationId, setOpenConversationId] = React.useState(null);
  const [unreadMessages, setUnreadMessages] = useUnreadMessages();

  // A tab link has done its job once the tab shows, so a reload does not keep reopening it.
  // One opened while the dashboard is already showing changes only the hash.
  React.useEffect(() => {
    const applyTabLink = () => {
      const tab = tabFromHash(window.location.hash, DASHBOARD_TABS.instructor);
      if (!tab) return;
      setSection(tab);
      setSelectedStudent(null);
      window.history.replaceState(null, '', DASHBOARD_HASH);
    };
    applyTabLink();
    window.addEventListener('hashchange', applyTabLink);
    return () => window.removeEventListener('hashchange', applyTabLink);
  }, []);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState(null);

  React.useEffect(() => {
    api.get('/dashboard/instructor')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // Booking payment flags by booking id (see withPaymentFlags). A failed read keeps the last flags.
  const [paymentFlags, setPaymentFlags] = React.useState(null);
  const loadPaymentFlags = React.useCallback(() => api.get('/bookings/instructor')
    .then(d => setPaymentFlags(new Map((d?.bookings || []).map(b => [b.id, b]))))
    .catch(() => {}), []);
  React.useEffect(() => { loadPaymentFlags(); }, [loadPaymentFlags]);

  const instructorData = data?.instructor || {};
  const insUser = instructorData?.user || {};
  const upcomingBookings = (data?.upcomingBookings || []).map(b => withPaymentFlags(b, paymentFlags));
  const upcoming = upcomingBookings.filter(b => new Date(b.scheduledAt) > new Date());
  const pending = upcomingBookings.filter(b => b.status === 'pending');
  const pastBookings = data?.pastBookings || [];
  const students = data?.students || [];
  // "Today" is whatever falls on the instructor's own calendar day (TTT-14) — the API returns
  // one forward-looking list and the split happens here, once, instead of per render slot.
  const todaysClasses = upcoming.filter(b => isSameZonedDay(b.scheduledAt, viewerTz));
  const laterClasses = upcoming.filter(b => !isSameZonedDay(b.scheduledAt, viewerTz));

  const navItems = withMessagesLabel(DASHBOARD_TABS.instructor, unreadMessages);
  const selectTab = (id) => { setSection(id); setSelectedStudent(null); };
  // Message on a booking started or continued a conversation: show it on the Messages tab.
  const openConversation = (conversation) => {
    setOpenConversationId(conversation?.id || null);
    selectTab(MESSAGES_TAB);
  };

  const SideNav = () => (
    <div style={{ width: 220, flexShrink: 0, background: T.white, borderRight: '1px solid ' + T.border, minHeight: 'calc(100vh - 64px)', padding: '24px 0' }}>
      <div style={{ padding: '0 16px 20px', borderBottom: '1px solid ' + T.border, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={instructorData.displayName || insUser.name} src={assetUrl(insUser.avatarUrl || instructorData.photo)} />
          <div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: 0 }}>{instructorData.displayName || insUser.name || 'Instructor'}</p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: 0 }}>● Instructor</p>
          </div>
        </div>
      </div>
      {navItems.map(item => (
        <button key={item.id} onClick={() => selectTab(item.id)}
          style={{
            width: '100%', padding: '11px 20px', border: 'none',
            background: section === item.id ? T.ivory : 'transparent',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            textAlign: 'left', borderRight: section === item.id ? '3px solid ' + T.terracotta : '3px solid transparent',
            transition: 'all 0.15s'
          }}>
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: section === item.id ? 600 : 400, fontSize: type.body, color: section === item.id ? T.terracotta : T.walnut }}>{item.label}</span>
        </button>
      ))}
      <div style={{ padding: '20px 16px 0', borderTop: '1px solid ' + T.border, marginTop: 12 }}>
        <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>← Back to site</button>
        <DashboardLogoutButton setPage={setPage} />
      </div>
    </div>
  );

  // ── Sessions (MS Teams-style schedule) ──────────────────────────────────────
  // The instructor's sessions, loaded when the Schedule tab opens (and after any change).
  const [mySessions, setMySessions] = React.useState(null);
  const [sessionForm, setSessionForm] = React.useState(null); // null | {id?, title, date, time, ...}
  // null | { field, message }. `field` names the form field the error belongs to, so it is read
  // out with that field (#345, DS-3); a null `field` is form-level (the server's answer).
  const [sessionError, setSessionError] = React.useState(null);
  const [scheduleView, setScheduleView] = React.useState('calendar'); // 'calendar' | 'list'
  const [calFocusDate, setCalFocusDate] = React.useState(null); // jump target after scheduling

  const loadSessions = React.useCallback(() => {
    api.get('/sessions/mine')
      .then(d => setMySessions(d?.sessions || []))
      .catch(() => setMySessions([]));
  }, []);
  React.useEffect(() => {
    if (section === 'schedule' && mySessions === null) loadSessions();
  }, [section, mySessions, loadSessions]);

  // "HH:MM" + minutes → "HH:MM" (capped at 23:59 — the form works within a single day).
  const addMinutes = (time, minutes) => {
    const [h, m] = time.split(':').map(Number);
    const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };
  const minutesBetween = (start, end) => {
    const [sh, sm] = String(start).split(':').map(Number);
    const [eh, em] = String(end).split(':').map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
  };

  const emptySessionForm = () => ({
    id: null,
    title: '',
    description: '',
    date: '',
    time: '',
    endTime: '',
    capacity: 1,
    // No prefill: the profile rate that used to seed this is gone (TTT-69), and guessing a price
    // for someone is worse than an empty field they have to fill in deliberately.
    price: '',
    level: '',
    meetingLink: '',
    // 1 publishes this session only; 2..12 publishes a weekly run at the same local time.
    repeatWeeks: 1,
    // Prefill the instructions from the profile default; editable per session.
    // The QR is deliberately not here (TTT-78). This form shows no QR field, so copying it in
    // snapshotted a value the instructor never saw and could not change — and any session made
    // before they uploaded a QR kept null forever. The server now falls back to the profile QR,
    // which is what the field note below has always promised.
    paymentInstructions: authUser?.instructor?.paymentInstructions || '',
  });

  // Prefill the form from an existing session, projected into the instructor's tz.
  // Duplicate lands a week later at the same local time: across a clock change that is not
  // 7 × 24 hours, so it moves by a calendar week in the instructor's tz.
  const sessionToForm = (s, { duplicate = false } = {}) => {
    const start = new Date(s.startAt);
    const base = duplicate ? addLocalWeeks(start, 1, viewerTz) : start;
    const p = utcToZonedParts(base, viewerTz);
    const startTime = `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
    return {
      id: duplicate ? null : s.id,
      title: s.title,
      description: s.description || '',
      date: `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`,
      time: startTime,
      endTime: addMinutes(startTime, s.duration || 60),
      capacity: s.capacity,
      price: s.price,
      level: s.level || '',
      meetingLink: s.meetingLink || '',
      repeatWeeks: 1,
      paymentInstructions: s.paymentInstructions || '',
    };
  };

  // Requests tab → "Publish a session": the same session form, prefilled from the time request on
  // the student's clock and shown in the instructor's. Saving it answers the request.
  const handlePublishFromRequest = (request) => {
    setSessionError(null);
    setSessionForm({
      ...emptySessionForm(),
      ...(requestToSessionSlot(request, viewerTz) || {}),
      fromRequest: {
        id: request.id,
        studentName: request.studentName,
        summary: `${formatRequestDays(request.days)} · ${formatRequestTimes(request.timesOfDay)} (${request.timezone})`,
      },
    });
    setSection('schedule');
  };

  // The session is saved before the answer is sent. A failed answer shows a banner whose Try again
  // re-sends it with the saved session id, so retrying never creates a second session.
  const { answer: answerRequestWithSession, banner: answerRetryBanner } = usePublishedAnswer(showSnackbar);

  // Shows the error under its field and moves focus there, so a screen reader reads both together.
  const failSessionField = (field, message) => {
    setSessionError({ field, message });
    document.getElementById(sessionFieldId(field))?.focus();
  };

  const handleSaveSession = async () => {
    const f = sessionForm;
    setSessionError(null);
    const startAt = zonedDateTimeToUtc(f.date, f.time, viewerTz);
    if (!f.title.trim()) return failSessionField('title', 'Please give the session a title.');
    if (!f.date) return failSessionField('date', 'Please pick a date.');
    if (!f.time) return failSessionField('time', 'Please pick a start time.');
    if (!startAt) return failSessionField('date', 'Please pick a date and time.');
    if (startAt.getTime() <= Date.now()) return failSessionField('date', 'The session must start in the future.');
    // Duration is derived from the start/end times (same-day sessions).
    if (!f.endTime) return failSessionField('endTime', 'Please pick an end time.');
    const duration = minutesBetween(f.time, f.endTime);
    if (duration < 15) return failSessionField('endTime', 'The session must end at least 15 minutes after it starts.');
    if (duration > 480) return failSessionField('endTime', 'Sessions can be at most 8 hours long.');
    // An edit changes one session; only a new session (or a duplicate) can repeat weekly.
    const repeatWeeks = f.id ? 1 : Number(f.repeatWeeks);
    if (!Number.isInteger(repeatWeeks) || repeatWeeks < 1) {
      return failSessionField('repeatWeeks', `Choose how many weeks to publish this session, from 1 to ${WEEKLY_RUN_MAX_WEEKS}.`);
    }
    if (repeatWeeks > WEEKLY_RUN_MAX_WEEKS) return failSessionField('repeatWeeks', WEEKLY_RUN_TOO_LONG);
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(),
        description: f.description.trim() || undefined,
        startAt: startAt.toISOString(),
        duration,
        capacity: Number(f.capacity),
        price: Number(f.price) || 0,
        // Always sent, so choosing "All levels" on an edit clears a level set before.
        level: f.level || null,
        ...(f.meetingLink.trim() ? { meetingLink: f.meetingLink.trim() } : {}),
        ...(f.paymentInstructions.trim() ? { paymentInstructions: f.paymentInstructions.trim() } : {}),
        // paymentQrUrl is never sent: leaving it out of a PATCH leaves any QR a session already
        // carries untouched, and a new session inherits the profile QR at read time instead.
      };
      if (f.id) {
        await api.patch(`/sessions/${f.id}`, payload);
        showSnackbar('Session updated.', { variant: 'success' });
      } else {
        // The server builds every week of the run at this local time in the instructor's tz.
        // `session` is the run's first week, the one a time request is answered with.
        const created = await api.post('/sessions', repeatWeeks > 1 ? { ...payload, repeatWeeks } : payload);
        if (f.fromRequest) await answerRequestWithSession(f.fromRequest, created?.session?.id);
        else showSnackbar(repeatWeeks > 1 ? `${repeatWeeks} weekly sessions scheduled.` : 'Session scheduled.', { variant: 'success' });
        // Jump the calendar to the new session (Teams-style).
        setCalFocusDate(startAt.toISOString());
      }
      setSessionForm(null);
      loadSessions();
    } catch (err) {
      // An ended sign-in already took them to the login page with its own notice (#502).
      if (isSignInExpired(err)) return;
      setSessionError({ field: null, message: err.data?.message || err.data?.error || err.message || 'Failed to save session' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSession = async (s) => {
    const booked = s.seatsBooked > 0;
    const ok = window.confirm(booked
      ? `Cancel "${s.title}"? ${s.seatsBooked} booked student(s) will be notified by email.`
      : `Cancel "${s.title}"?`);
    if (!ok) return;
    try {
      await api.patch(`/sessions/${s.id}/cancel`);
      showSnackbar('Session cancelled.', { variant: 'success' });
      loadSessions();
    } catch (err) {
      if (isSignInExpired(err)) return;
      showSnackbar('Failed to cancel: ' + (err.data?.message || err.message), { variant: 'error' });
    }
  };

  // Meeting links for confirmed bookings
  const [meetingLinks, setMeetingLinks] = React.useState({});
  React.useEffect(() => {
    if (data?.upcomingBookings) {
      const links = {};
      data.upcomingBookings.forEach(b => {
        if (b.meetingLink) links[b.id] = b.meetingLink;
      });
      setMeetingLinks(links);
    }
  }, [data]);
  const updateMeetingLink = (bookingId, url) => {
    setMeetingLinks(prev => ({ ...prev, [bookingId]: url }));
  };
  const saveMeetingLink = async (bookingId) => {
    const url = meetingLinks[bookingId];
    if (!url) return;
    try {
      await api.patch(`/bookings/${bookingId}/meeting-link`, { meetingLink: url });
      showSnackbar('Meeting link saved.', { variant: 'success' });
    } catch (err) {
      if (isSignInExpired(err)) return;
      showSnackbar('Failed to save link.', { variant: 'error' });
    }
  };

  // PaymentProofPanel confirms or declines a pending booking, then reports here. A 409 means the
  // booking changed elsewhere (the student cancelled), so the list refreshes either way.
  const handleBookingChanged = async ({ status, message }) => {
    showSnackbar(message, { variant: status === 'conflict' ? 'error' : 'success' });
    loadPaymentFlags();
    try {
      setData(await api.get('/dashboard/instructor'));
    } catch (err) {
      if (isSignInExpired(err)) return;
      showSnackbar('We could not refresh your bookings. Reload the page to see the latest status.', { variant: 'error' });
    }
  };

  // After a student note is saved (StudentNoteEditor owns the request and its messages).
  const refreshDashboard = async () => {
    const refreshed = await api.get('/dashboard/instructor');
    setData(refreshed);
  };


  // Reviews
  const [instructorReviews, setInstructorReviews] = React.useState([]);
  React.useEffect(() => {
    if (section === 'reviews') {
      api.get('/reviews/instructor/me')
        .then(data => setInstructorReviews(data?.reviews || []))
        .catch(() => setInstructorReviews([]));
    }
  }, [section]);

  // ── Earnings (Payments tab, TTT-17) ────────────────────────────────────────
  const [earnings, setEarnings] = React.useState(null);
  const [earningsError, setEarningsError] = React.useState(false);

  const loadEarnings = React.useCallback(() => {
    setEarningsError(false);
    api.get('/dashboard/instructor/earnings')
      .then(d => setEarnings(d))
      // Never silently show ₱0 for money that exists — an empty page and a failed page must
      // not look identical to someone checking what they earned.
      .catch(() => { setEarnings(null); setEarningsError(true); });
  }, []);
  // Refetch every time the tab opens, not just the first time. Confirming a payment on the
  // Bookings tab moves these numbers, and stale money is worse than one extra request.
  React.useEffect(() => {
    if (section === 'payments') loadEarnings();
  }, [section, loadEarnings]);

  // Buckets are derived in the *viewer's* timezone. Bucketing on the server would repeat TTT-14,
  // where the server's midnight decided which day a Manila instructor's class belonged to.
  const earningsView = React.useMemo(() => {
    const rows = earnings?.bookings || [];
    const monthKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
    const now = utcToZonedParts(new Date(), viewerTz);

    // Twelve buckets ending on the viewer's current month, so an empty month still draws a gap.
    const buckets = new Map();
    for (let back = 11; back >= 0; back--) {
      let month = now.month - back;
      let year = now.year;
      while (month <= 0) { month += 12; year -= 1; }
      buckets.set(monthKey(year, month), { key: monthKey(year, month), label: MONTH_SHORT[month - 1], received: 0, awaiting: 0 });
    }

    const thisMonthKey = monthKey(now.year, now.month);
    const bySession = new Map();
    let thisMonth = 0;

    rows.forEach(b => {
      const parts = utcToZonedParts(new Date(b.scheduledAt), viewerTz);
      const key = monthKey(parts.year, parts.month);
      const received = b.status === 'confirmed' || b.status === 'completed';
      const bucket = buckets.get(key);
      if (bucket) {
        if (received) bucket.received += b.price;
        else if (b.status === 'pending') bucket.awaiting += b.price;
      }
      if (!received) return;
      if (key === thisMonthKey) thisMonth += b.price;
      const title = b.sessionTitle || 'Other';
      bySession.set(title, (bySession.get(title) || 0) + b.price);
    });

    const topSessions = [...bySession.entries()]
      .map(([title, received]) => ({ title, received }))
      .sort((a, b) => b.received - a.received)
      .slice(0, 5)
      .reverse(); // a vertical recharts layout draws the first row at the bottom
    return { months: [...buckets.values()], topSessions, thisMonth };
  }, [earnings, viewerTz]);

  // Profile form state
  const [profileForm, setProfileForm] = React.useState({
    name: '',
    displayName: '',
    timezone: 'Asia/Manila',
    avatarUrl: '',
    photo: '',
    bio: '',
    teachingStyle: '',
    tagline: '',
    country: '',
    basedIn: '',
    flag: '',
    language: '',
    teaches: '',
    native: '',
    fluent: '',
    conversational: '',
    specialties: [],
    credentials: '',
    interests: '',
    certifications: '',
    levels: '',
    paymentInstructions: '',
    paymentQrUrl: '',
    responseTime: '',
    nameJP: '',
    nameZH: '',
    resume: '',
    videoUrl: '',
    gallery: [],
    socialLinks: {},
    welcomeMessage: '',
  });

  React.useEffect(() => {
    if (authUser?.instructor) {
      const ins = authUser.instructor;
      setProfileForm({
        name: authUser.name || '',
        displayName: ins.displayName || '',
        timezone: authUser.timezone || 'Asia/Manila',
        avatarUrl: authUser.avatarUrl || '',
        photo: ins.photo || '',
        bio: ins.bio || '',
        teachingStyle: ins.teachingStyle || '',
        tagline: ins.tagline || '',
        country: ins.country || '',
        basedIn: ins.basedIn || '',
        flag: ins.flag || '',
        language: ins.language || '',
        teaches: (ins.teaches || []).join(', '),
        native: (ins.native || []).join(', '),
        fluent: (ins.fluent || []).join(', '),
        conversational: (ins.conversational || []).join(', '),
        specialties: ins.specialties || [],
        credentials: (ins.credentials || []).join(', '),
        interests: (ins.interests || []).join(', '),
        certifications: (ins.certifications || []).join(', '),
        levels: (ins.levels || []).join(', '),
        paymentInstructions: ins.paymentInstructions || '',
        paymentQrUrl: ins.paymentQrUrl || '',
        responseTime: ins.responseTime || '',
        nameJP: ins.nameJP || '',
        nameZH: ins.nameZH || '',
        resume: Array.isArray(ins.resume) ? JSON.stringify(ins.resume, null, 2) : (ins.resume || ''),
        videoUrl: ins.videoUrl || '',
        gallery: ins.gallery || [],
        socialLinks: ins.socialLinks || {},
        welcomeMessage: ins.welcomeMessage || '',
      });
    }
  }, [authUser]);

  const handleProfileChange = (field, value) => {
    setProfileError(null);
    setProfileFieldError(null);
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  // `profileError` is form-level (the server's answer). `profileFieldError` ({ field, message })
  // is a link the form refuses itself, read out with its field (#345, DS-3).
  const [profileError, setProfileError] = React.useState(null);
  const [profileFieldError, setProfileFieldError] = React.useState(null);
  const profileField = (field) => fieldProps(profileFieldId(field), profileFieldError?.field === field ? profileFieldError.message : '');
  const profileFieldErrorText = (field) => (profileFieldError?.field === field
    ? <p {...profileField(field).errorProps} style={fieldErrorStyle}>{profileFieldError.message}</p>
    : null);

  // The Payments tab saves the same profile without showing these fields; there the error is
  // form-level, so it never hides under a field that is not on the page.
  const failProfileField = (field, message) => {
    const input = document.getElementById(profileFieldId(field));
    if (!input) return setProfileError(message);
    setProfileFieldError({ field, message });
    input.focus();
  };

  const handleSaveProfile = async () => {
    setProfileError(null);
    setProfileFieldError(null);
    if (profileForm.videoUrl && !isSafeLinkUrl(profileForm.videoUrl)) return failProfileField('videoUrl', LINK_MUST_BE_WEB('intro video'));
    const badLink = SOCIAL_LINKS.find(({ key }) => profileForm.socialLinks?.[key] && !isSafeLinkUrl(profileForm.socialLinks[key]));
    if (badLink) return failProfileField(`social-${badLink.key}`, LINK_MUST_BE_WEB(badLink.label));
    setSaving(true);
    try {
      // Only the essential + kept-optional fields. Removed fields (nameJP/nameZH, totalStudents/
      // totalHours, responseTime, basedIn, resume, interests, levels) are simply not sent, so any
      // existing values are preserved by the API's per-key update.
      const listCsv = (v) => (v ? v.split(',').map(s => s.trim()).filter(Boolean) : []);
      const payload = {
        timezone: profileForm.timezone,
        avatarUrl: profileForm.avatarUrl || null,
        displayName: profileForm.displayName.trim(),
        bio: profileForm.bio.trim(),
        teachingStyle: profileForm.teachingStyle.trim(),
        tagline: profileForm.tagline || null,
        country: profileForm.country.trim(),
        flag: COUNTRY_FLAG[profileForm.country] || profileForm.flag || '',
        language: profileForm.language.trim(),
        teaches: listCsv(profileForm.teaches),
        native: listCsv(profileForm.native),
        fluent: listCsv(profileForm.fluent),
        specialties: profileForm.specialties,
        credentials: listCsv(profileForm.credentials),
        certifications: listCsv(profileForm.certifications),
        paymentInstructions: profileForm.paymentInstructions || null,
        photo: profileForm.photo || null,
        videoUrl: profileForm.videoUrl || null,
        gallery: profileForm.gallery || [],
        socialLinks: profileForm.socialLinks || null,
        welcomeMessage: profileForm.welcomeMessage || null,
      };
      if (profileForm.name) payload.name = profileForm.name;
      await updateProfile(payload);
      const refreshed = await api.get('/dashboard/instructor');
      setData(refreshed);
      showSnackbar('Profile updated successfully.', { variant: 'success' });
    } catch (err) {
      // The sign-in ended: the app shows the login page's notice, so the server's own
      // "Please log in to continue." stays off this page (#502).
      if (isSignInExpired(err)) return;
      const msg = err.data?.message || err.message || 'Failed to save profile';
      setProfileError(msg);
      showSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setProfileError(null);
    try {
      const res = await api.post('/profile/upload-avatar', formData);
      setProfileForm(prev => ({ ...prev, avatarUrl: res.url }));
      await updateProfile({ avatarUrl: res.url });
      const refreshed = await api.get('/dashboard/instructor');
      setData(refreshed);
      showSnackbar('Avatar uploaded successfully.', { variant: 'success' });
    } catch (err) {
      if (isSignInExpired(err)) return;
      const msg = err.data?.message || err.message || 'Failed to upload avatar';
      setProfileError(msg);
      showSnackbar(msg, { variant: 'error' });
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (profileForm.gallery.length + files.length > 6) {
      showSnackbar('Maximum 6 gallery photos allowed.', { variant: 'error' });
      return;
    }
    setProfileError(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('file', f));
      const res = await api.post('/profile/upload-gallery', formData);
      const newUrls = res.urls || [];
      const updatedGallery = [...profileForm.gallery, ...newUrls];
      setProfileForm(prev => ({ ...prev, gallery: updatedGallery }));
      showSnackbar(`${newUrls.length} photo(s) uploaded.`, { variant: 'success' });
    } catch (err) {
      if (isSignInExpired(err)) return;
      const msg = err.data?.message || err.message || 'Failed to upload gallery photos';
      setProfileError(msg);
      showSnackbar(msg, { variant: 'error' });
    }
  };

  const handleGalleryDelete = (url) => {
    setProfileForm(prev => ({ ...prev, gallery: prev.gallery.filter(u => u !== url) }));
  };

  const handlePaymentQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/profile/upload-payment-qr', formData);
      setProfileForm(prev => ({ ...prev, paymentQrUrl: res.url }));
      showSnackbar('Payment QR uploaded.', { variant: 'success' });
    } catch (err) {
      if (isSignInExpired(err)) return;
      showSnackbar(err.data?.message || err.message || 'Failed to upload QR', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, flexDirection: isMobile ? 'column' : 'row' }}>
      {!isMobile && <SideNav />}
      <DashboardMenu tabs={navItems} active={section} onSelect={selectTab} setPage={setPage} />
      <div style={{ flex: 1, padding: isMobile ? '16px' : '32px', overflowY: 'auto', maxHeight: isMobile ? 'none' : 'calc(100vh - 64px)', paddingBottom: '32px' }}>

        {/* A session saved for a time request whose answer did not go through: Try again lives here,
            above every tab, until the answer is sent or the instructor closes it. */}
        {answerRetryBanner}

        {/* Action prompt: only while not yet listed. Once the profile is complete (status flips
            to active) this disappears — the listed/verified state then lives on the Profile page. */}
        {instructorData.status === 'pending' && (
          <div style={{ marginBottom: 24, padding: '12px 16px', borderRadius: 12, background: T.warmGrey, border: '1px solid ' + T.caramelGold, color: T.walnut, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span>Complete your profile (bio, teaching style, languages, rates) to get listed in the marketplace.</span>
            <button onClick={() => setSection('profile')} style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 8, padding: '8px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Complete profile →</button>
          </div>
        )}

        {/* ── Home ── */}
        {section === 'home' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 4px' }}>{greetingFor(new Date(), authUser?.timezone)}, {instructorData.displayName || insUser.name || 'Instructor'}</h1>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, margin: 0 }}>Here&apos;s your overview for today.</p>
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 16, marginBottom: 24 }}>
              {[
                { label: 'Active students', value: students.length, sub: `${students.length} total`, color: T.ivory },
                { label: 'Sessions today', value: todaysClasses.length, sub: 'Scheduled today', color: T.warmGrey },
                { label: 'Pending requests', value: pending.length, sub: 'Awaiting response', color: T.ivory },
                // 'Total hours' stat removed (TTT-39): it read a stored, instructor-editable column.
              ].map(s => (
                <div key={s.label} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 18px' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.body, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif", marginBottom: 10 }}>●</div>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 4px' }}>{s.label}</p>
                  <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 0 2px' }}>{s.value}</p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.walnut, margin: 0 }}>{s.sub}</p>
                </div>
              ))}
            </div>

            {/* Today's schedule + Upcoming */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Today&apos;s sessions</h3>
                {todaysClasses.length === 0 && (
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>No sessions today.</p>
                )}
                {todaysClasses.map(cls => (
                  <div key={cls.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid ' + T.border }}>
                    <Avatar name={cls.studentName} src={assetUrl(cls.student?.user?.avatarUrl)} size={44} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{cls.studentName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>{formatInTz(cls.scheduledAt, viewerTz, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })} · {cls.duration || 60} min · {cls.session?.title || cls.sessionType}</p>
                      {cls.studentEmail && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: '2px 0 0' }}>{cls.studentEmail}</p>}
                    </div>
                    {cls.meetingLink ? (
                      <JoinLink meetingLink={cls.meetingLink} showHost={false} />
                    ) : (
                      <Btn size="sm" variant="ghost" disabled>No link</Btn>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: 0 }}>Upcoming bookings</h3>
                  <button onClick={() => setSection('bookings')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.terracotta }}>See all →</button>
                </div>
                {laterClasses.slice(0, 4).map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + T.border }}>
                    <Avatar name={b.studentName} src={assetUrl(b.student?.user?.avatarUrl)} size={32} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{b.studentName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>{formatInTz(b.scheduledAt, viewerTz)} · {b.session?.title || b.sessionType}</p>
                    </div>
                    {b.status === 'pending' ? <PaymentSentBadge booking={b} /> : <Badge variant="sage">Confirmed</Badge>}
                  </div>
                ))}
                {laterClasses.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>No upcoming bookings.</p>}
              </div>
            </div>
          </div>
        )}

        {/* ── Schedule (MS Teams-style sessions) ── */}
        {section === 'schedule' && (() => {
          const sessions = mySessions || [];
          const now = Date.now();
          const isPastOrCancelled = (s) => s.status === 'cancelled' || new Date(s.startAt).getTime() <= now;
          const upcomingSessions = sessions.filter(s => !isPastOrCancelled(s)).sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
          const pastSessions = sessions.filter(isPastOrCancelled);

          const SessionCard = (s) => {
            const cancelled = s.status === 'cancelled';
            const past = new Date(s.startAt).getTime() <= now;
            return (
              <div key={s.id} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '18px 20px', opacity: cancelled ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: 0 }}>{s.title}</p>
                      <Badge variant="subtle">{sessionLevelLabel(s.level)}</Badge>
                      {cancelled && <Badge variant="default">Cancelled</Badge>}
                      {!cancelled && s.seatsLeft === 0 && <Badge variant="gold">Full</Badge>}
                    </div>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '4px 0 0' }}>
                      {formatInTz(s.startAt, viewerTz)} · {s.duration} min · ₱{(s.price ?? 0).toLocaleString()} / student
                    </p>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '2px 0 0' }}>
                      {s.seatsBooked}/{s.capacity} seats booked
                      {/* Same two pending states as the Bookings tab's badge: payment sent or awaiting payment. */}
                      {paymentSentCount(s) > 0 && ` · ${paymentSentCount(s)} payment sent`}
                      {awaitingPaymentCount(s) > 0 && ` · ${awaitingPaymentCount(s)} awaiting payment`}
                    </p>
                    {(s.bookings || []).length > 0 && (
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: '6px 0 0' }}>
                        {s.bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').map(b => `${b.studentName} (${bookingStateLabel(b)})`).join(', ')}
                      </p>
                    )}
                  </div>
                  {!cancelled && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {!past && <Btn size="sm" variant="ghost" onClick={() => { setSessionError(null); setSessionForm(sessionToForm(s)); }}>Edit</Btn>}
                      <Btn size="sm" variant="ghost" onClick={() => { setSessionError(null); setSessionForm(sessionToForm(s, { duplicate: true })); }}>Duplicate</Btn>
                      {!past && <Btn size="sm" variant="ghost" onClick={() => handleCancelSession(s)}>Cancel</Btn>}
                    </div>
                  )}
                </div>
              </div>
            );
          };

          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: 0 }}>Schedule</h2>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Calendar / List toggle (Teams-style calendar is the default) */}
                  <div style={{ display: 'flex', gap: 4, background: T.white, borderRadius: 10, padding: 4, border: '1px solid ' + T.border }}>
                    {[['calendar', 'Calendar'], ['list', 'List']].map(([k, l]) => (
                      <button key={k} onClick={() => setScheduleView(k)}
                        style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: scheduleView === k ? T.terracotta : 'transparent', color: scheduleView === k ? T.white : T.walnut, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.label, cursor: 'pointer', transition: 'all 0.15s' }}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <Btn onClick={() => { setSessionError(null); setSessionForm(emptySessionForm()); }}>+ New session</Btn>
                </div>
              </div>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 20px' }}>
                Schedule sessions like meeting invites — students see them on your profile in their own timezone and book a seat.
                <span style={{ color: T.terracotta, fontWeight: 600 }}> Your timezone: {viewerTz}</span>
              </p>

              {mySessions === null ? (
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading sessions…</p>
              ) : scheduleView === 'calendar' ? (
                <LazyPanel fallback={<p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading calendar…</p>}>
                <SessionCalendar
                  sessions={sessions}
                  tz={viewerTz}
                  focusDate={calFocusDate}
                  onSessionClick={(s) => {
                    if (s.status === 'cancelled') return;
                    setSessionError(null);
                    // Past sessions can't be edited (start must stay future) — offer duplicate instead.
                    if (new Date(s.startAt).getTime() <= Date.now()) setSessionForm(sessionToForm(s, { duplicate: true }));
                    else setSessionForm(sessionToForm(s));
                  }}
                  onSlotClick={(date, time) => {
                    setSessionError(null);
                    setSessionForm({ ...emptySessionForm(), date, time, endTime: addMinutes(time, 60) });
                  }}
                />
                </LazyPanel>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {upcomingSessions.length === 0 && (
                    <div style={{ background: T.white, borderRadius: 16, border: '1px dashed ' + T.border, padding: '32px 20px', textAlign: 'center' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: 0 }}>No upcoming sessions. Create one so students can book you.</p>
                    </div>
                  )}
                  {upcomingSessions.map(SessionCard)}
                  {pastSessions.length > 0 && (
                    <>
                      <h4 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '16px 0 0' }}>Past & cancelled</h4>
                      {pastSessions.map(SessionCard)}
                    </>
                  )}
                </div>
              )}

              {/* New / edit session form, on the shared dialog (#345). A click outside never closes
                  it, so a stray click cannot lose what was typed; Escape and Close do, unless saving. */}
              {sessionForm && (() => {
                const errorFor = (field) => (sessionError?.field === field ? sessionError.message : '');
                const titleField = fieldProps(sessionFieldId('title'), errorFor('title'));
                const dateField = fieldProps(sessionFieldId('date'), errorFor('date'));
                const timeField = fieldProps(sessionFieldId('time'), errorFor('time'));
                const endField = fieldProps(sessionFieldId('endTime'), errorFor('endTime'));
                const weeksField = fieldProps(sessionFieldId('repeatWeeks'), errorFor('repeatWeeks'));
                // Date, start and end share one row, so their error shows under the row.
                const whenError = [['date', dateField], ['time', timeField], ['endTime', endField]].find(([field]) => errorFor(field));
                const editing = sessionForm.id ? sessions.find(s => s.id === sessionForm.id) : null;
                return (
                <Modal maxWidth={520} labelledBy={SESSION_FORM_TITLE_ID} closeOnBackdrop={false}
                  onClose={saving ? undefined : () => setSessionForm(null)}>
                  <div style={{ padding: 28 }}>
                    <h3 id={SESSION_FORM_TITLE_ID} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 40px 4px 0' }}>{sessionForm.id ? 'Edit session' : 'New session'}</h3>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 18px' }}>Times are in your timezone ({viewerTz}). Students see them in theirs.</p>
                    {sessionForm.fromRequest && (
                      <div data-testid="session-from-request" style={{ background: T.ivory, borderRadius: 12, border: '1px solid ' + T.border, padding: '10px 14px', marginBottom: 14 }}>
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, margin: 0 }}>
                          For {sessionForm.fromRequest.studentName}’s time request: {sessionForm.fromRequest.summary}.
                        </p>
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>
                          The date and time below are converted to your timezone. When you schedule it, {sessionForm.fromRequest.studentName} gets an email with a link to book a seat.
                        </p>
                      </div>
                    )}

                    <div style={{ marginBottom: 14 }}>
                      <label {...titleField.labelProps} style={labelStyle}>Title{reqStar}
                        <input {...titleField.inputProps} value={sessionForm.title} onChange={e => setSessionForm(f => ({ ...f, title: e.target.value }))} maxLength={200} placeholder="e.g. Beginner Japanese — Hiragana Kickstart" style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                      {errorFor('title') && <p {...titleField.errorProps} style={fieldErrorStyle}>{errorFor('title')}</p>}
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Description
                        <textarea value={sessionForm.description} onChange={e => setSessionForm(f => ({ ...f, description: e.target.value }))} rows={2} maxLength={5000} placeholder="What will you cover?" style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }} />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 4 }}>
                      <label {...dateField.labelProps} style={labelStyle}>Date{reqStar}
                        <input {...dateField.inputProps} type="date" aria-label="Date"value={sessionForm.date} onChange={e => setSessionForm(f => ({ ...f, date: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                      <label {...timeField.labelProps} style={labelStyle}>Start time{reqStar}
                        <input {...timeField.inputProps} type="time" aria-label="Start time"value={sessionForm.time}
                          onChange={e => setSessionForm(f => ({
                            ...f,
                            time: e.target.value,
                            // Keep a sensible end while typing: default/track +1h until the user sets it apart.
                            endTime: !f.endTime || f.endTime === addMinutes(f.time || e.target.value, 60) ? addMinutes(e.target.value, 60) : f.endTime,
                          }))}
                          style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                      <label {...endField.labelProps} style={labelStyle}>End time{reqStar}
                        <input {...endField.inputProps} type="time" aria-label="End time"value={sessionForm.endTime} onChange={e => setSessionForm(f => ({ ...f, endTime: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                    </div>
                    {whenError && <p {...whenError[1].errorProps} style={{ ...fieldErrorStyle, margin: '0 0 4px' }}>{errorFor(whenError[0])}</p>}
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '0 0 14px' }}>
                      {sessionForm.time && sessionForm.endTime && minutesBetween(sessionForm.time, sessionForm.endTime) > 0
                        ? `Duration: ${minutesBetween(sessionForm.time, sessionForm.endTime)} minutes`
                        : 'Duration is computed from the start and end times.'}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                      <label style={labelStyle}>Max students{reqStar}
                        <input type="number" min={1} max={100} value={sessionForm.capacity} onChange={e => setSessionForm(f => ({ ...f, capacity: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                      <label style={labelStyle}>Price (₱){reqStar}
                        <input type="number" min={0} step={50} value={sessionForm.price} onChange={e => setSessionForm(f => ({ ...f, price: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Level
                        <select value={sessionForm.level} onChange={e => setSessionForm(f => ({ ...f, level: e.target.value }))} style={{ ...inputStyle, marginTop: 6 }}>
                          {SESSION_LEVEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>Students filter by level. Choose All levels if anyone can join.</p>
                    </div>
                    {!sessionForm.id && (
                      <div style={{ marginBottom: 14 }}>
                        <label {...weeksField.labelProps} style={labelStyle}>Repeat weekly (weeks)
                          <input {...weeksField.inputProps} type="number" inputMode="numeric" min={1} max={WEEKLY_RUN_MAX_WEEKS} step={1} value={sessionForm.repeatWeeks}
                            onChange={e => setSessionForm(f => ({ ...f, repeatWeeks: e.target.value }))}
                            style={{ ...inputStyle, marginTop: 6 }} />
                        </label>
                        {errorFor('repeatWeeks') && <p {...weeksField.errorProps} style={fieldErrorStyle}>{errorFor('repeatWeeks')}</p>}
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>
                          {Number(sessionForm.repeatWeeks) > WEEKLY_RUN_MAX_WEEKS
                            ? `The most is ${WEEKLY_RUN_MAX_WEEKS} weeks.`
                            : Number(sessionForm.repeatWeeks) > 1
                            ? `Publishes ${sessionForm.repeatWeeks} sessions, one each week at the same time. Each has its own seats.`
                            : `1 publishes this session only. Choose up to ${WEEKLY_RUN_MAX_WEEKS} to publish one each week at the same time.`}
                        </p>
                      </div>
                    )}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Meeting link
                        <input value={sessionForm.meetingLink} onChange={e => setSessionForm(f => ({ ...f, meetingLink: e.target.value }))} placeholder="https://… (Zoom / Meet / Teams — can be added later)" style={{ ...inputStyle, marginTop: 6 }} />
                      </label>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>Only students with a confirmed booking see this.</p>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Payment instructions
                        <textarea value={sessionForm.paymentInstructions} onChange={e => setSessionForm(f => ({ ...f, paymentInstructions: e.target.value }))} rows={3} maxLength={5000} placeholder="GCash number, bank details… (prefilled from your Pricing settings)" style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }} />
                      </label>
                      {authUser?.instructor?.paymentQrUrl && (
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>Your payment QR (from Pricing settings) is shown to students alongside these instructions.</p>
                      )}
                    </div>

                    {sessionError && !sessionError.field && (
                      <div role="alert" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '10px 14px', marginBottom: 14 }}>
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{sessionError.message}</p>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                      <Btn variant="ghost" onClick={() => setSessionForm(null)} style={{ flex: 1, justifyContent: 'center' }} disabled={saving}>Close</Btn>
                      <Btn onClick={handleSaveSession} disabled={saving} style={{ flex: 2, justifyContent: 'center' }}>
                        {saving ? 'Saving…' : sessionForm.id ? 'Save changes' : Number(sessionForm.repeatWeeks) > 1 ? 'Schedule sessions' : 'Schedule session'}
                      </Btn>
                    </div>
                    {editing && (
                      <button
                        onClick={() => { setSessionForm(null); handleCancelSession(editing); }}
                        disabled={saving}
                        style={{ marginTop: 12, width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.errorRed, padding: 4 }}>
                        Cancel this session{editing.seatsBooked > 0 ? ` — ${editing.seatsBooked} booked student(s) will be notified` : ''}
                      </button>
                    )}
                  </div>
                </Modal>
                );
              })()}
            </div>
          );
        })()}

        {/* ── Requests (time requests from students) ── */}
        {section === 'requests' && (
          <TimeRequestsPanel viewerTz={viewerTz} onPublish={handlePublishFromRequest} />
        )}

        {/* ── Bookings ── */}
        {section === 'bookings' && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>Bookings</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {pending.map(b => (
                <div key={b.id} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 0, flexWrap: 'wrap' }}>
                    <Avatar name={b.studentName} src={assetUrl(b.student?.user?.avatarUrl)} size={44} />
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{b.studentName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>{b.session?.title ? `${b.session.title} · ` : ''}{formatInTz(b.scheduledAt, viewerTz)} · ₱{(b.price ?? 0).toLocaleString()}</p>
                      {b.studentEmail && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: '2px 0 0' }}>{b.studentEmail}</p>}
                      {b.notes && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '4px 0 0', fontStyle: 'italic' }}>"{b.notes}"</p>}
                    </div>
                    <PaymentProofPanel booking={b} onChanged={handleBookingChanged} />
                    <MessageButton bookingId={b.id} otherName={b.studentName || 'your student'} onStarted={openConversation} />
                  </div>
                </div>
              ))}
              {upcoming.filter(b => b.status === 'confirmed').map(b => (
                <div key={b.id} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
                    <Avatar name={b.studentName} src={assetUrl(b.student?.user?.avatarUrl)} size={44} />
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{b.studentName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>{b.session?.title ? `${b.session.title} · ` : ''}{formatInTz(b.scheduledAt, viewerTz)} · ₱{(b.price ?? 0).toLocaleString()}</p>
                      {b.studentEmail && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.terracotta, margin: '2px 0 0' }}>{b.studentEmail}</p>}
                    </div>
                    {/* Where a pending booking shows its screenshot: JTPay's Paid stands in for it (JTP-23). */}
                    <PaidThroughRopidaBadge booking={b} />
                    <Badge variant="sage">Confirmed</Badge>
                    <MessageButton bookingId={b.id} otherName={b.studentName || 'your student'} onStarted={openConversation} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 58 }}>
                    <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, flexShrink: 0 }} title="Overrides the session's meeting link for this student only — set the shared link on the session itself (Schedule tab).">Meeting link (this booking):</span>
                    <input
                      value={meetingLinks[b.id] || ''}
                      onChange={e => updateMeetingLink(b.id, e.target.value)}
                      onBlur={() => saveMeetingLink(b.id)}
                      onKeyDown={e => { if (e.key === 'Enter') saveMeetingLink(b.id); }}
                      placeholder="Paste Zoom / Meet / Teams link…"
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }}
                    />
                    {/* Instructor-typed and about to become an href — same guard as the student
                        side, so a typo'd scheme is refused here too rather than only at save. */}
                    {isSafeLinkUrl(meetingLinks[b.id]) && (
                      <a href={meetingLinks[b.id]} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.terracotta, textDecoration: 'none' }}>Open →</a>
                    )}
                  </div>
                </div>
              ))}
              {pending.length === 0 && upcoming.length === 0 && (
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No upcoming bookings.</p>
              )}
            </div>

            {/* Past: completed bookings, newest first. A booking moves here on its own an hour
                after its session ends (RV-6). */}
            <section data-testid="instructor-past-bookings" aria-labelledby="instructor-past-heading">
              <h3 id="instructor-past-heading" style={h3Style}>Past</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pastBookings.map(b => (
                  <div key={b.id} data-testid="instructor-past-booking" style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <Avatar name={b.studentName} src={assetUrl(b.student?.user?.avatarUrl)} />
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{b.studentName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>{b.session?.title ? `${b.session.title} · ` : ''}{formatInTz(b.scheduledAt, viewerTz)}</p>
                    </div>
                    <Badge variant="sage">Completed</Badge>
                    <MessageButton bookingId={b.id} otherName={b.studentName || 'your student'} onStarted={openConversation} />
                  </div>
                ))}
                {pastBookings.length === 0 && (
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: 0 }}>No past bookings yet. A confirmed booking moves here an hour after its session ends.</p>
                )}
              </div>
            </section>
          </div>
        )}

        {/* ── Messages ── */}
        {section === MESSAGES_TAB && (
          <MessagesPanel key={openConversationId || 'list'} initialConversationId={openConversationId} onUnreadChange={setUnreadMessages} />
        )}

        {/* ── Students ── */}
        {section === 'students' && !selectedStudent && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>Students</h2>
            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.ivory }}>
                    {['Student', 'Email', 'Last session', 'Next session', 'Level', ''].map(h => (
                      <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid ' + T.border }}
                      onMouseEnter={e => e.currentTarget.style.background = T.ivory}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar name={s.user?.name || 'Student'} src={assetUrl(s.user?.avatarUrl)} size={32} />
                          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>{s.user?.name || 'Student'}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.terracotta }}>{s.user?.email || '—'}</td>
                      <td style={{ padding: '14px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut }}>{s.lastClass || '—'}</td>
                      <td style={{ padding: '14px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.espresso, fontWeight: 600 }}>{s.nextClass || '—'}</td>
                      <td style={{ padding: '14px 16px' }}><Badge variant="blue">{s.level || 'N/A'}</Badge></td>
                      <td style={{ padding: '14px 16px' }}>
                        <Btn size="sm" variant="subtle" onClick={() => setSelectedStudent(s.id)}>View →</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {students.length === 0 && (
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No students yet.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Student Detail ── */}
        {section === 'students' && selectedStudent && (() => {
          const s = students.find(st => st.id === selectedStudent);
          if (!s) return null;
          return (
            <div>
              <button onClick={() => setSelectedStudent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, padding: 0 }}>← All students</button>
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 28, marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
                  <Avatar name={s.user?.name || 'Student'} src={assetUrl(s.user?.avatarUrl)} size={52} />
                  <div>
                    <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: 0 }}>{s.user?.name || 'Student'}</h2>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0 }}>Level {s.level || 'N/A'}</p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                  {[['Last session', s.lastClass || '—'], ['Next session', s.nextClass || '—']].map(([k, v]) => (
                    <div key={k} style={{ background: T.ivory, borderRadius: 12, padding: '12px 16px' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 4px' }}>{k}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
              <StudentNoteEditor studentId={s.id} note={s.note} onSaved={refreshDashboard} />
            </div>
          );
        })()}

        {/* ── Reviews ── */}
        {section === 'reviews' && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>Reviews</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {instructorReviews.length === 0 && (
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No reviews yet.</p>
              )}
              {instructorReviews.map(r => (
                <div key={r.id} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.ivory, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.body, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif" }}>
                      {(r.student?.user?.name || 'S').charAt(0)}
                    </div>
                    <div>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{r.student?.user?.name || 'Student'}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: 0 }}>{new Date(r.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <span style={{ color: T.terracotta, fontSize: type.lead }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                    </div>
                  </div>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: 0 }}>{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Payments: what you earned, and where students send it ── */}
        {section === 'payments' && (() => {
          const totals = earnings?.totals || { received: 0, awaiting: 0, cancelled: 0 };
          const rows = earnings?.bookings || [];
          const hasMoney = !!earnings && (rows.length > 0 || totals.received > 0 || totals.awaiting > 0 || totals.cancelled > 0);
          const cardStyle = { background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 };
          const statusBadge = { confirmed: 'sage', completed: 'sage', pending: 'gold', cancelled: 'subtle' };

          return (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 6px' }}>Payments</h2>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 24px' }}>
              Students pay you directly — Ropida takes no cut. These are the amounts they agreed to pay when they booked.
            </p>

            {earningsError && (
              <div role="alert" style={{ ...cardStyle, border: '1px dashed ' + T.border, textAlign: 'center', marginBottom: 24 }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, margin: '0 0 4px' }}>We could not load your earnings.</p>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: '0 0 16px' }}>Your money is safe — this page just failed to fetch it. Try again.</p>
                <Btn size="sm" onClick={loadEarnings}>Try again</Btn>
              </div>
            )}

            {!earnings && !earningsError && (
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, marginBottom: 24 }}>Loading your earnings…</p>
            )}

            {earnings && !hasMoney && (
              <div style={{ ...cardStyle, border: '1px dashed ' + T.border, textAlign: 'center', marginBottom: 24 }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 15, color: T.espresso, margin: '0 0 4px' }}>Nobody has booked a seat yet.</p>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: '0 0 16px' }}>Schedule a session and set its price — earnings show up here once students book.</p>
                <Btn size="sm" onClick={() => setSection('schedule')}>Schedule a session →</Btn>
              </div>
            )}

            {hasMoney && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                  {[
                    { label: 'Received', value: totals.received, sub: totals.cancelled > 0 ? `${peso(totals.cancelled)} lost to cancellations` : 'Confirmed and completed bookings' },
                    { label: 'Awaiting payment', value: totals.awaiting, sub: 'Students who have not paid yet' },
                    { label: 'This month', value: earningsView.thisMonth, sub: 'Received in your timezone' },
                  ].map(s => (
                    <div key={s.label} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 18px' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 6px' }}>{s.label}</p>
                      <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 26, color: T.espresso, margin: '0 0 4px' }}>{peso(s.value)}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.walnut, margin: 0 }}>{s.sub}</p>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap: 20, marginBottom: 20 }}>
                  <div style={cardStyle}>
                    <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 4px' }}>Earnings by month</h3>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 12px' }}>Last 12 months, by the date each session runs.</p>
                    <LazyPanel fallback={<p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading chart…</p>}>
                      <MonthlyEarningsChart months={earningsView.months} />
                    </LazyPanel>
                  </div>
                  <div style={cardStyle}>
                    <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 4px' }}>Your best-earning sessions</h3>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 12px' }}>Money received, top 5.</p>
                    {earningsView.topSessions.length === 0 ? (
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Nothing received yet.</p>
                    ) : (
                      <LazyPanel fallback={<p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading chart…</p>}>
                        <TopSessionsChart sessions={earningsView.topSessions} />
                      </LazyPanel>
                    )}
                  </div>
                </div>

                <div style={{ ...cardStyle, marginBottom: 24, overflowX: 'auto' }}>
                  <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 4px' }}>Every booking</h3>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 14px' }}>
                    Last 12 months, newest first. The totals above cover every booking you have ever taken.
                  </p>
                  {rows.length === 0 ? (
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>No bookings in the last 12 months.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                      <thead>
                        <tr>
                          {['Date', 'Student', 'Session', 'Amount', 'Status'].map((h, i) => (
                            <th key={h} style={{ textAlign: i >= 3 ? 'right' : 'left', padding: '8px 10px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, fontWeight: 600, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: '1px solid ' + T.border }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(b => (
                          <tr key={b.id}>
                            <td style={{ padding: '10px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, borderBottom: '1px solid ' + T.border, whiteSpace: 'nowrap' }}>{formatInTz(b.scheduledAt, viewerTz, { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                            <td style={{ padding: '10px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, borderBottom: '1px solid ' + T.border }}>{b.studentName}</td>
                            <td style={{ padding: '10px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, borderBottom: '1px solid ' + T.border }}>{b.sessionTitle || '—'}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontWeight: 600, color: T.espresso, borderBottom: '1px solid ' + T.border, whiteSpace: 'nowrap' }}>{peso(b.price)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid ' + T.border }}><Badge variant={statusBadge[b.status] || 'subtle'}>{b.status}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* Single column: the rates half went with TTT-69, payment details is all that's left. */}
            <div style={{ maxWidth: 560 }}>
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 28 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: '0 0 6px' }}>Payment details (GCash / bank)</h3>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 16px' }}>You set the price on each session you schedule. This is where students send the money.</p>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('paymentInstructions').labelProps} style={labelStyle}>Payment instructions</label>
                  <textarea {...profileField('paymentInstructions').inputProps} value={profileForm.paymentInstructions} onChange={e => handleProfileChange('paymentInstructions', e.target.value)} rows={3} maxLength={5000}
                    placeholder={'e.g. GCash: 0917 123 4567 (Your Name)\nBPI savings 1234-5678-90\nSend a screenshot after paying.'}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '6px 0 0' }}>Prefilled on every new session you schedule. Students see this after booking, so they know where to send payment.</p>
                </div>
                <div style={{ marginBottom: 16 }}>
                  {/* A heading, not a <label>: the upload button below carries its own name. */}
                  <span style={labelStyle}>Payment QR code</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {profileForm.paymentQrUrl && (
                      <img src={assetUrl(profileForm.paymentQrUrl)} alt="Payment QR" style={{ width: 84, height: 84, borderRadius: 10, border: '1px solid ' + T.border, objectFit: 'cover' }} />
                    )}
                    <label style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, cursor: 'pointer', background: T.ivory }}>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePaymentQrUpload} />
                      {profileForm.paymentQrUrl ? 'Replace QR image' : 'Upload QR image'}
                    </label>
                  </div>
                </div>

                {profileError && (
                  <div role="alert" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '12px 16px', marginBottom: 16 }}>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{profileError}</p>
                  </div>
                )}
                <Btn onClick={handleSaveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save payment details'}</Btn>
              </div>
            </div>
          </div>
          );
        })()}

        {/* ── Profile ── */}
        {section === 'profile' && (
          <div>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 20px' }}>Your Profile</h2>

            {/* Listing + verification status so the instructor always knows where they stand. */}
            {(() => {
              const listed = instructorData.status === 'active';
              const verified = instructorData.verified;
              const missing = [];
              if (!profileForm.displayName.trim()) missing.push('Display name');
              if (!profileForm.country.trim()) missing.push('Country');
              if (!profileForm.language.trim()) missing.push('Teaching language');
              if (!profileForm.teaches.trim()) missing.push('Languages you teach');
              if (!profileForm.bio.trim()) missing.push('Bio');
              if (!profileForm.teachingStyle.trim()) missing.push('Teaching style');
              const pill = { padding: '6px 12px', borderRadius: 9999, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 700 };
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                  <span style={{ ...pill, background: listed ? alpha('sageGreen', 'tint') : T.warmGrey, border: '1px solid ' + (listed ? T.sageGreen : T.caramelGold), color: listed ? T.sageGreen : T.walnut }}>
                    {listed ? '✓ Listed in the marketplace' : '○ Not listed yet'}
                  </span>
                  {verified ? (
                    <span style={{ ...pill, background: alpha('sageGreen', 'tint'), border: '1px solid ' + T.sageGreen, color: T.sageGreen }}>✓ Verified by Ropida</span>
                  ) : listed ? (
                    <span style={{ ...pill, fontWeight: 600, background: T.warmGrey, border: '1px solid ' + T.border, color: T.walnut }}>Verification pending — an admin will review your profile</span>
                  ) : null}
                  {!listed && missing.length > 0 && (
                    <span style={{ width: '100%', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut }}>
                      Fill the required (<span style={{ color: T.errorRed }}>*</span>) fields to get listed. Still needed: <strong>{missing.join(', ')}</strong>
                    </span>
                  )}
                  {/* IW-2: the public profile address, to bring their own students. */}
                  <CopyProfileLinkButton instructor={instructorData} />
                </div>
              );
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24, alignItems: 'start' }}>
              {/* Form */}
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 28 }}>
                {/* Avatar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                  <Avatar name={profileForm.displayName || insUser.name} src={assetUrl(profileForm.avatarUrl)} size={64} label="Your profile photo" />
                  <div>
                    <label style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, cursor: 'pointer', background: T.ivory }}>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
                      Upload photo
                    </label>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '6px 0 0' }}>JPG, PNG up to 5MB</p>
                  </div>
                </div>

                <h3 style={{ ...h3Style, margin: '0 0 4px' }}>Essentials</h3>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 16px' }}>Required (<span style={{ color: T.errorRed }}>*</span>) to get listed in the marketplace.</p>

                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('displayName').labelProps} style={labelStyle}>Display name{reqStar}</label>
                  <input {...profileField('displayName').inputProps} value={profileForm.displayName} onChange={e => handleProfileChange('displayName', e.target.value)} placeholder="Name students will see" style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label {...profileField('country').labelProps} style={labelStyle}>Country{reqStar}</label>
                    <select {...profileField('country').inputProps} value={profileForm.country} onChange={e => { handleProfileChange('country', e.target.value); handleProfileChange('flag', COUNTRY_FLAG[e.target.value] || ''); }} style={selectStyle}>
                      <option value="">Select country…</option>
                      {/* A stored country outside this list (admin-created or seeded rows carry
                          things like "Japan (Osaka)") otherwise left the dropdown showing the
                          placeholder while the public profile showed the real value — the same
                          field reading two ways (TTT-55). Show what's actually saved. */}
                      {profileForm.country && !COUNTRY_FLAG[profileForm.country] && (
                        <option value={profileForm.country}>{profileForm.flag} {profileForm.country}</option>
                      )}
                      {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label {...profileField('language').labelProps} style={labelStyle}>Teaching language{reqStar}</label>
                    <select {...profileField('language').inputProps} value={profileForm.language} onChange={e => handleProfileChange('language', e.target.value)} style={selectStyle}>
                      <option value="">Select language…</option>
                      {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('teaches').labelProps} style={labelStyle}>Languages you teach{reqStar}</label>
                  <input {...profileField('teaches').inputProps} value={profileForm.teaches} onChange={e => handleProfileChange('teaches', e.target.value)} placeholder="Japanese, Mandarin" style={inputStyle} />
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>Comma-separated.</p>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('bio').labelProps} style={labelStyle}>Bio{reqStar}</label>
                  <textarea {...profileField('bio').inputProps} value={profileForm.bio} onChange={e => handleProfileChange('bio', e.target.value)} rows={4} placeholder="Introduce yourself to students…" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('teachingStyle').labelProps} style={labelStyle}>Teaching style{reqStar}</label>
                  <textarea {...profileField('teachingStyle').inputProps} value={profileForm.teachingStyle} onChange={e => handleProfileChange('teachingStyle', e.target.value)} rows={3} placeholder="How do you run your lessons?" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <h3 style={h3Style}>Optional details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <span style={labelStyle}>Timezone</span>
                    <TimezoneSelect value={profileForm.timezone} onChange={(tz) => handleProfileChange('timezone', tz)} style={selectStyle} />
                  </div>
                  <div>
                    <label {...profileField('name').labelProps} style={labelStyle}>Account name</label>
                    <input {...profileField('name').inputProps} value={profileForm.name} onChange={e => handleProfileChange('name', e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('tagline').labelProps} style={labelStyle}>Tagline</label>
                  <input {...profileField('tagline').inputProps} value={profileForm.tagline} onChange={e => handleProfileChange('tagline', e.target.value)} placeholder="Short headline for your profile" style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label {...profileField('native').labelProps} style={labelStyle}>Native languages</label>
                    <input {...profileField('native').inputProps} value={profileForm.native} onChange={e => handleProfileChange('native', e.target.value)} placeholder="Japanese" style={inputStyle} />
                  </div>
                  <div>
                    <label {...profileField('fluent').labelProps} style={labelStyle}>Fluent languages</label>
                    <input {...profileField('fluent').inputProps} value={profileForm.fluent} onChange={e => handleProfileChange('fluent', e.target.value)} placeholder="English" style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  {/* Picked from the list students filter on (IW-3), so a specialty always finds its students. */}
                  <span id="profile-specialties-label" style={labelStyle}>Specialties</span>
                  <SpecialtyPicker labelId="profile-specialties-label" value={profileForm.specialties} onChange={v => handleProfileChange('specialties', v)} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('credentials').labelProps} style={labelStyle}>Credentials</label>
                  <input {...profileField('credentials').inputProps} value={profileForm.credentials} onChange={e => handleProfileChange('credentials', e.target.value)} placeholder="MA Linguistics, 10 years experience" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('certifications').labelProps} style={labelStyle}>Certifications</label>
                  <input {...profileField('certifications').inputProps} value={profileForm.certifications} onChange={e => handleProfileChange('certifications', e.target.value)} placeholder="JLPT N1, HSK 6" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('videoUrl').labelProps} style={labelStyle}>Intro video URL (YouTube or Loom)</label>
                  <input {...profileField('videoUrl').inputProps} value={profileForm.videoUrl} onChange={e => handleProfileChange('videoUrl', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." style={inputStyle} />
                  {profileFieldErrorText('videoUrl')}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <span style={labelStyle}>Photo gallery ({profileForm.gallery.length}/6)</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {profileForm.gallery.map(url => (
                      <div key={url} style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: '1px solid ' + T.border }}>
                        <img src={assetUrl(url)} alt="Gallery" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button onClick={() => handleGalleryDelete(url)} style={{ position: 'absolute', top: 2, right: 2, background: alpha('espresso', 'scrim'), color: T.white, border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: type.label, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      </div>
                    ))}
                    {profileForm.gallery.length < 6 && (
                      <label style={{ width: 80, height: 80, borderRadius: 10, border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: T.ivory }}>
                        <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryUpload} />
                        <span style={{ fontSize: type.h3, color: T.driftwood }}>+</span>
                      </label>
                    )}
                  </div>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>JPG, PNG up to 5MB each. Max 6 photos.</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  {SOCIAL_LINKS.map(({ key, label }) => (
                    <div key={key}>
                      <label {...profileField(`social-${key}`).labelProps} style={labelStyle}>{label}</label>
                      <input {...profileField(`social-${key}`).inputProps} value={profileForm.socialLinks?.[key] || ''} onChange={e => handleProfileChange('socialLinks', { ...profileForm.socialLinks, [key]: e.target.value })} placeholder={`https://${key}.com/...`} style={inputStyle} />
                      {profileFieldErrorText(`social-${key}`)}
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label {...profileField('welcomeMessage').labelProps} style={labelStyle}>Welcome message</label>
                  <textarea {...profileField('welcomeMessage').inputProps} value={profileForm.welcomeMessage} onChange={e => handleProfileChange('welcomeMessage', e.target.value.slice(0, 500))} rows={3} placeholder="Write a short welcome message for prospective students..." style={{ ...inputStyle, resize: 'vertical' }} />
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0', textAlign: 'right' }}>{profileForm.welcomeMessage.length}/500</p>
                </div>

                {profileError && (
                  <div role="alert" style={{ background: alpha('errorRed', 'tint'), borderRadius: 12, border: '1px solid ' + alpha('errorRed', 'selection'), padding: '12px 16px', marginBottom: 16 }}>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{profileError}</p>
                  </div>
                )}
                <Btn onClick={handleSaveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</Btn>
              </div>

              {/* Preview */}
              <div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.walnut, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 16px' }}>Marketplace preview</h3>
                {(() => {
                  // Start from the saved row and overlay the unsaved edits, so the preview is the
                  // real card — verified badge, specialty chips and true listing status included.
                  // Re-mapping the fields by hand here is what made the preview quietly disagree
                  // with what students actually see (TTT-55).
                  const csv = (v) => (v ? v.split(',').map(s => s.trim()).filter(Boolean) : []);
                  const previewIns = {
                    ...instructorData,
                    displayName: profileForm.displayName || profileForm.name || 'Instructor',
                    photo: profileForm.avatarUrl || profileForm.photo,
                    flag: profileForm.flag,
                    country: profileForm.country,
                    language: profileForm.language,
                    teaches: csv(profileForm.teaches),
                    native: csv(profileForm.native),
                    specialties: profileForm.specialties,
                    bio: profileForm.bio,
                    tagline: profileForm.tagline,
                  };
                  return <InstructorCard instructor={previewIns} />;
                })()}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export { InstructorDashboard };
